import { ErrorMessage, Field, FieldProps } from 'formik';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { InfoBubble } from './InfoBubble';

export interface TextEditorProps {
  label?: string;
  isRequired?: boolean;
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  useFormik?: boolean;
  error?: string | boolean;
  helper?: string;
}

export const TextEditor = forwardRef<ReactQuill, TextEditorProps>(
  (
    {
      value,
      label,
      isRequired,
      name,
      onChange,
      disabled,
      placeholder,
      useFormik = true,
      error,
      helper,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const [text, setText] = useState('');

    if (useFormik && name) {
      // Utilisation avec Formik
      return (
        <div className="max-w-6xl">
          {label && (
            <label className="text-xs font-medium text-gray-600">
              {label || t('textEditor:title')}
              {isRequired && <span className="ml-1 text-danger">*</span>}
            </label>
          )}
          <Field name={name}>
            {({ field, form, meta }: FieldProps) => (
              <>
                <ReactQuill
                  ref={ref}
                  className={`mt-1.5 rounded-lg border p-2 shadow-sm ${meta.touched && meta.error ? 'border-red-500' : 'border-gray-300'}`}
                  value={field.value || ''}
                  onChange={val => form.setFieldValue(name, val)}
                  theme="snow"
                  placeholder={placeholder || t('textEditor:placeholder')}
                  readOnly={disabled}
                />
                <ErrorMessage
                  name={name}
                  component="span"
                  className="text-xs text-danger md:text-sm"
                />
                {helper && <InfoBubble message={helper} className="mt-2" />}
              </>
            )}
          </Field>
        </div>
      );
    }

    // Utilisation sans Formik
    return (
      <div className="max-w-6xl">
        <label className="text-xs font-medium text-gray-600">
          {label || t('textEditor:title')}
          {isRequired && <span className="ml-1 text-danger">*</span>}
        </label>
        <ReactQuill
          ref={ref}
          className={`mt-1.5 rounded-lg border p-2 shadow-sm ${error ? 'border-red-500' : 'border-gray-300'}`}
          value={value || text}
          onChange={onChange ? onChange : setText}
          theme="snow"
          placeholder={placeholder || t('textEditor:placeholder')}
          readOnly={disabled}
        />
        {error && (
          <span className="text-xs text-danger md:text-sm">{error}</span>
        )}
        {helper && <InfoBubble message={helper} className="mt-2" />}
      </div>
    );
  },
);
